import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Row,
    Col,
    Button,
    Form,
    InputNumber,
    Input,
    DatePicker,
    ConfigProvider,
    Table,
    Checkbox,
    Radio,
    Rate,
    Space,
} from 'antd';
import {
    CalendarOutlined,
    SaveOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getProjectsPartnership, createOrUpdateProjectsPartnership } from '../../core/center-accreditation/centerAccreditationApi';

import './ScProjectsPartnership.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Text } = Typography;
const { TextArea } = Input;

// Default project types (6.1)
const defaultProjectTypes = [
    { code: 'regioniniai_lokaliniai', title: '6.1.1 Regioniniai ir/ar lokaliniai', projects_count: 0, partners_count: 0 },
    { code: 'salies', title: '6.1.2 Šalies', projects_count: 0, partners_count: 0 },
    { code: 'tarptautiniai', title: '6.1.3 Tarptautiniai', projects_count: 0, partners_count: 0 },
    { code: 'es_fondai', title: '6.1.4 Europos Sąjungos struktūrinių fondų projektai', projects_count: 0, partners_count: 0 },
];

// Project roles (6.2)
const projectRoles = [
    { code: 'vykdo_savo', title: '6.2.1 Vykdo savo organizacijos parengtus projektus', number: '6.2.1' },
    { code: 'realizuoja_partneris', title: '6.2.2 Realizuoja projektus kaip partneris', number: '6.2.2' },
    { code: 'skleidzia_info', title: '6.2.3 Skleidžia informaciją apie projektus', number: '6.2.3' },
    { code: 'padeda_rasti', title: '6.2.4 Padeda rasti partnerius', number: '6.2.4' },
    { code: 'organizuoja_rengima', title: '6.2.5 Organizuoja projekto rengimą', number: '6.2.5' },
    { code: 'konsultuoja', title: '6.2.6 Konsultuoja rengiant ir realizuojant projektus', number: '6.2.6' },
    { code: 'koordinuoja', title: '6.2.7 Koordinuoja projektus', number: '6.2.7' },
    { code: 'vertina', title: '6.2.8 Vertina projektus ir jų vykdymą', number: '6.2.8' },
    { code: 'kita', title: '6.2.9 Kita (įrašykite)', number: '6.2.9' },
];

// Project objectives (6.3)
const projectObjectives = [
    { code: 'tobulinti_mokyklos_veikla', title: '6.3.1 Tobulinti mokyklos kaip organizacijos veiklą' },
    { code: 'tobulinti_mokytojo_kompetencija', title: '6.3.2 Tobulinti mokytojo kompetenciją' },
    { code: 'ismokyti_komandoje', title: '6.3.3 Išmokyti mokytojus dirbti komandoje' },
    { code: 'pripratinti_projektiniam', title: '6.3.4 Pripratinti prie projektinio darbo režimo' },
    { code: 'ismokyti_planuoti', title: '6.3.5 Išmokyti numatyti veiklos perspektyvą bei planuoti savo veiklą derinant ją su kitų veikla' },
    { code: 'ismokyti_tinklus', title: '6.3.6 Išmokyti žmones kurti partnerystės tinklus ir juose veikti' },
    { code: 'ismokyti_kulturos_ivairove', title: '6.3.7 Išmokyti žmones priimti kultūrinę įvairovę' },
    { code: 'kita', title: '6.3.8 Kita (įrašykite)' },
];

// Project results for institution (6.4)
const projectResultsForInstitution = [
    { code: 'materialinis_aprupinimas', title: '6.4.1 Pagerintas institucijos materialinis aprūpinimas' },
    { code: 'darbuotoju_kompetencija', title: '6.4.2 Pakilo institucijos darbuotojų kompetencija' },
    { code: 'renginiu_kokybe', title: '6.4.3 Pakilo institucijos renginių kokybė' },
    { code: 'renginiu_apimtys', title: '6.4.4 Padidėjo institucijos renginių apimtys' },
    { code: 'partnerystes_rysiai', title: '6.4.5 Užmegzti partnerystės ryšiai su kitomis institucijomis' },
    { code: 'kita', title: '6.4.6 Kita (įrašykite)' },
];

// Partnership networks scale (6.5)
const partnershipNetworksScale = [
    { code: 'regioniniai_lokaliniai', title: '6.5.1 Regioninių ir/ar lokalinių' },
    { code: 'salies', title: '6.5.2 Šalies' },
    { code: 'tarptautiniu', title: '6.5.3 Tarptautinių' },
];

// Partnership networks nature (6.6)
const partnershipNetworksNature = [
    { code: 'oficialiuose', title: '6.6.1 Oficialiuose (pavyzdžiui, sudarytuose ir patvirtintuose Europos Sąjungos, Lietuvos švietimo projektuose)' },
    { code: 'formaliuose', title: '6.6.2 Formaliuose (sudarytuose pačių organizacijų iniciatyva, tačiau nepagrįstų kokiais nors finansuojamais projektais)' },
    { code: 'neformaliuose', title: '6.6.3 Neformaliuose (susidariusiuose institucijai bendraujant su dalykiniais partneriais, tačiau šių santykių neformalizuojant partnerystės tinklo sukūrimo dokumentais)' },
];

// Project results for organizations (6.8)
const projectResultsForOrganizations = [
    { code: 'materialine_baze', title: '6.8.1 Organizacijos pagerino materialinę bazę' },
    { code: 'kompetencijos', title: '6.8.2 Prasiplėtė (kai kurių) organizacijos narių kompetencijos' },
    { code: 'struktura_efektyvesne', title: '6.8.3 Organizacijos struktūra pasidarė efektyvesnė' },
    { code: 'naujos_galimybes', title: '6.8.4 Atsirado galimybės naujai organizacijos veiklai' },
    { code: 'partnerystes_rysiai', title: '6.8.5 Užmegzti partnerystės ryšiai su organizacijomis' },
    { code: 'kita', title: '6.8.6 Kita (įrašykite)' },
];

// Project results for individuals (6.10)
const projectResultsForIndividuals = [
    { code: 'naujos_kompetencijos', title: '6.10.1 Įgijo naujų kompetencijų' },
    { code: 'patobulino_kompetencijas', title: '6.10.2 Patobulino turimas kompetencijas' },
    { code: 'praplete_akirati', title: '6.10.3 Praplėtė akiratį' },
    { code: 'kita', title: '6.10.4 Kita (įrašykite)' },
];

const ScProjectsPartnership: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { projectsPartnership, isProjectsPartnershipFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);

    console.log('projectsPartnership', projectsPartnership);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());
    const [partnershipNetworksScaleTotal, setPartnershipNetworksScaleTotal] = useState<number>(0);
    const [partnershipNetworksNatureTotal, setPartnershipNetworksNatureTotal] = useState<number>(0);
    const [stimulatedOrganizationsCount, setStimulatedOrganizationsCount] = useState<number | null>(null);
    const [stimulatedIndividualsCount, setStimulatedIndividualsCount] = useState<number | null>(null);
    const [effectivenessResearch, setEffectivenessResearch] = useState<string | null>(null);
    const [projectResultsForInstitutionState, setProjectResultsForInstitutionState] = useState<string[]>([]);
    const [projectResultsForOrganizationsState, setProjectResultsForOrganizationsState] = useState<string[]>([]);

    useEffect(() => {
        dispatch(getProjectsPartnership({ date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null }));
    }, [dispatch, selectedDate]);

    useEffect(() => {
        if (projectsPartnership) {
            // Load data from partnership_networks (6.5, 6.6)
            let partnershipNetworks: {
                partnership_networks_scale?: Array<{ code: string; title?: string; count: number | string }>;
                partnership_networks_nature?: Array<{ code: string; title?: string; count: number | string }>;
            } = {};
            if (projectsPartnership.partnership_networks && typeof projectsPartnership.partnership_networks === 'object' && !Array.isArray(projectsPartnership.partnership_networks)) {
                partnershipNetworks = projectsPartnership.partnership_networks as {
                    partnership_networks_scale?: Array<{ code: string; title?: string; count: number | string }>;
                    partnership_networks_nature?: Array<{ code: string; title?: string; count: number | string }>;
                };
            }

            // Convert partnership_networks_scale from array of objects to form object format
            const partnershipNetworksScaleValue: { [key: string]: number } = {};
            if (partnershipNetworks.partnership_networks_scale && Array.isArray(partnershipNetworks.partnership_networks_scale)) {
                partnershipNetworks.partnership_networks_scale.forEach((item: any) => {
                    partnershipNetworksScaleValue[item.code] = Number(item.count) || 0;
                });
            } else if (projectsPartnership.partnership_networks_scale) {
                // Handle old format (object or array)
                if (Array.isArray(projectsPartnership.partnership_networks_scale)) {
                    // Convert array ["3", "0"] to object using partnershipNetworksScale order
                    partnershipNetworksScale.forEach((scale, index) => {
                        const value = projectsPartnership.partnership_networks_scale?.[index];
                        partnershipNetworksScaleValue[scale.code] = value !== undefined ? Number(value) || 0 : 0;
                    });
                } else if (typeof projectsPartnership.partnership_networks_scale === 'object' && projectsPartnership.partnership_networks_scale !== null) {
                    Object.keys(projectsPartnership.partnership_networks_scale).forEach((key) => {
                        const value = projectsPartnership.partnership_networks_scale?.[key];
                        partnershipNetworksScaleValue[key] = value !== undefined ? Number(value) || 0 : 0;
                    });
                }
            }

            // Convert partnership_networks_nature from array of objects to form object format
            const partnershipNetworksNatureValue: { [key: string]: number } = {};
            if (partnershipNetworks.partnership_networks_nature && Array.isArray(partnershipNetworks.partnership_networks_nature)) {
                partnershipNetworks.partnership_networks_nature.forEach((item: any) => {
                    partnershipNetworksNatureValue[item.code] = Number(item.count) || 0;
                });
            } else if (projectsPartnership.partnership_networks_nature) {
                // Handle old format (object or array)
                if (Array.isArray(projectsPartnership.partnership_networks_nature)) {
                    // Convert array ["6", "0"] to object using partnershipNetworksNature order
                    partnershipNetworksNature.forEach((nature, index) => {
                        const value = projectsPartnership.partnership_networks_nature?.[index];
                        partnershipNetworksNatureValue[nature.code] = value !== undefined ? Number(value) || 0 : 0;
                    });
                } else if (typeof projectsPartnership.partnership_networks_nature === 'object' && projectsPartnership.partnership_networks_nature !== null) {
                    Object.keys(projectsPartnership.partnership_networks_nature).forEach((key) => {
                        const value = projectsPartnership.partnership_networks_nature?.[key];
                        partnershipNetworksNatureValue[key] = value !== undefined ? Number(value) || 0 : 0;
                    });
                }
            }
            const projectResultsForInstitutionValue = projectsPartnership.partners || [];
            const projectResultsForOrganizationsValue = projectsPartnership.project_results_for_organizations || [];

            // Transform project_types to ensure numbers are correct (API might return strings)
            const transformedProjectTypes = (projectsPartnership.project_types || defaultProjectTypes).map((type: any) => ({
                code: type.code,
                title: type.title,
                projects_count: Number(type.projects_count) || 0,
                partners_count: Number(type.partners_count) || 0,
            }));

            // Handle stimulated_organizations_stats - convert array to object if needed
            let stimulatedOrganizationsStatsValue = projectsPartnership.stimulated_organizations_stats || {};
            if (Array.isArray(stimulatedOrganizationsStatsValue)) {
                // Convert array ["2", "0"] to object {"mokyklos": 2, "kitos_istaigos": 0}
                stimulatedOrganizationsStatsValue = {
                    mokyklos: Number(stimulatedOrganizationsStatsValue[0]) || 0,
                    kitos_istaigos: Number(stimulatedOrganizationsStatsValue[1]) || 0,
                };
            } else if (typeof stimulatedOrganizationsStatsValue === 'object' && stimulatedOrganizationsStatsValue !== null) {
                // Ensure all values are numbers
                const statsObj: { [key: string]: number } = {};
                Object.keys(stimulatedOrganizationsStatsValue).forEach((key) => {
                    statsObj[key] = Number(stimulatedOrganizationsStatsValue[key]) || 0;
                });
                stimulatedOrganizationsStatsValue = statsObj;
            }

            // If stimulated_organizations_count exists, add it to stats as 'total'
            if (projectsPartnership.stimulated_organizations_count) {
                stimulatedOrganizationsStatsValue = {
                    ...stimulatedOrganizationsStatsValue,
                    total: Number(projectsPartnership.stimulated_organizations_count) || 0,
                };
            }

            // Load data from project_dissemination_forms (6.9, 6.10, 6.11)
            let projectDisseminationForms: {
                stimulated_individuals_count?: number | null;
                project_results_for_individuals?: string[] | null;
                participation_trend?: string | null;
            } = {};
            if (projectsPartnership.project_dissemination_forms && typeof projectsPartnership.project_dissemination_forms === 'object' && !Array.isArray(projectsPartnership.project_dissemination_forms)) {
                projectDisseminationForms = projectsPartnership.project_dissemination_forms as {
                    stimulated_individuals_count?: number | null;
                    project_results_for_individuals?: string[] | null;
                    participation_trend?: string | null;
                };
            }
            const stimulatedIndividualsCountValue = projectDisseminationForms.stimulated_individuals_count !== undefined
                ? projectDisseminationForms.stimulated_individuals_count
                : (projectsPartnership.stimulated_individuals_count || null);
            const projectResultsForIndividualsValue = projectDisseminationForms.project_results_for_individuals !== undefined
                ? projectDisseminationForms.project_results_for_individuals
                : (projectsPartnership.project_results_for_individuals || []);
            const participationTrendValue = projectDisseminationForms.participation_trend !== undefined
                ? projectDisseminationForms.participation_trend
                : (projectsPartnership.participation_trend || null);

            form.setFieldsValue({
                project_types: transformedProjectTypes,
                project_roles_ratings: projectsPartnership.project_roles_ratings || {},
                project_objectives: projectsPartnership.project_objectives || [],
                partners: projectResultsForInstitutionValue,
                partnership_networks_scale: partnershipNetworksScaleValue,
                partnership_networks_nature: partnershipNetworksNatureValue,
                stimulated_organizations_stats: stimulatedOrganizationsStatsValue,
                project_results_for_organizations: projectResultsForOrganizationsValue,
                stimulated_individuals_count: stimulatedIndividualsCountValue,
                project_results_for_individuals: projectResultsForIndividualsValue,
                participation_trend: participationTrendValue,
                project_evaluation_methods: projectsPartnership.project_evaluation_methods || [],
            });

            // Update state values
            setPartnershipNetworksScaleTotal(Object.values(partnershipNetworksScaleValue).reduce((sum: number, value: any) => sum + (Number(value) || 0), 0));
            setPartnershipNetworksNatureTotal(Object.values(partnershipNetworksNatureValue).reduce((sum: number, value: any) => sum + (Number(value) || 0), 0));
            const totalCount = (stimulatedOrganizationsStatsValue as { total?: number }).total;
            setStimulatedOrganizationsCount(totalCount || null);
            setStimulatedIndividualsCount(stimulatedIndividualsCountValue);
            setEffectivenessResearch(projectsPartnership.project_evaluation_methods && projectsPartnership.project_evaluation_methods.length > 0 ? 'taip' : 'ne');
            setProjectResultsForInstitutionState(projectResultsForInstitutionValue);
            setProjectResultsForOrganizationsState(projectResultsForOrganizationsValue);
        } else {
            form.setFieldsValue({
                project_types: defaultProjectTypes,
                project_roles_ratings: {},
                project_objectives: [],
                partners: [],
                partnership_networks_scale: {},
                partnership_networks_nature: {},
                stimulated_organizations_stats: {},
                project_results_for_organizations: [],
                stimulated_individuals_count: null,
                project_results_for_individuals: [],
                participation_trend: null,
                project_evaluation_methods: [],
            });

            // Reset state values
            setPartnershipNetworksScaleTotal(0);
            setPartnershipNetworksNatureTotal(0);
            setStimulatedOrganizationsCount(null);
            setStimulatedIndividualsCount(null);
            setEffectivenessResearch(null);
            setProjectResultsForInstitutionState([]);
            setProjectResultsForOrganizationsState([]);
        }
    }, [projectsPartnership, form]);

    const handleSave = async (values: any) => {
        // Transform project_types to ensure numbers are correct
        const projectTypes = (values.project_types || []).map((type: any) => ({
            code: type.code,
            title: type.title,
            projects_count: Number(type.projects_count) || 0,
            partners_count: Number(type.partners_count) || 0,
        }));

        // Form partnership_networks object from 6.5 and 6.6 fields (as arrays of objects like 6.1)
        // Ensure partnership_networks_scale is an object, not a number
        let partnershipNetworksScaleValue = values.partnership_networks_scale || {};
        if (typeof partnershipNetworksScaleValue === 'number') {
            partnershipNetworksScaleValue = {};
        }

        // Transform partnership_networks_scale to array of objects
        const partnershipNetworksScaleArray = partnershipNetworksScale.map((scale: any) => {
            const countValue = partnershipNetworksScaleValue[scale.code];
            return {
                code: scale.code,
                title: scale.title,
                count: countValue !== undefined && countValue !== null ? Number(countValue) : 0,
            };
        });

        // Ensure partnership_networks_nature is an object
        let partnershipNetworksNatureValue = values.partnership_networks_nature || {};
        if (typeof partnershipNetworksNatureValue === 'number') {
            partnershipNetworksNatureValue = {};
        }

        // Transform partnership_networks_nature to array of objects
        const partnershipNetworksNatureArray = partnershipNetworksNature.map((nature: any) => {
            const countValue = partnershipNetworksNatureValue[nature.code];
            return {
                code: nature.code,
                title: nature.title,
                count: countValue !== undefined && countValue !== null ? Number(countValue) : 0,
            };
        });

        // Combine scale and nature into partnership_networks object
        const partnershipNetworks: {
            partnership_networks_scale?: Array<{ code: string; title: string; count: number }>;
            partnership_networks_nature?: Array<{ code: string; title: string; count: number }>;
        } = {};

        if (partnershipNetworksScaleArray.length > 0) {
            partnershipNetworks.partnership_networks_scale = partnershipNetworksScaleArray;
        }

        if (partnershipNetworksNatureArray.length > 0) {
            partnershipNetworks.partnership_networks_nature = partnershipNetworksNatureArray;
        }

        // Ensure project_roles_ratings values are numbers
        const rolesRatings: { [key: string]: number } = {};
        if (values.project_roles_ratings) {
            Object.keys(values.project_roles_ratings).forEach((key) => {
                rolesRatings[key] = Number(values.project_roles_ratings[key]) || 0;
            });
        }

        // Ensure stimulated_organizations_stats values are numbers and it's an object, not an array
        const orgStats: { mokyklos?: number; kitos_istaigos?: number; total?: number; [key: string]: number | undefined } = {};
        if (values.stimulated_organizations_stats) {
            // If it's an array, convert to object
            if (Array.isArray(values.stimulated_organizations_stats)) {
                orgStats.mokyklos = Number(values.stimulated_organizations_stats[0]) || 0;
                orgStats.kitos_istaigos = Number(values.stimulated_organizations_stats[1]) || 0;
            } else if (typeof values.stimulated_organizations_stats === 'object' && values.stimulated_organizations_stats !== null) {
                Object.keys(values.stimulated_organizations_stats).forEach((key) => {
                    orgStats[key] = Number(values.stimulated_organizations_stats[key]) || 0;
                });
            }
        }

        // Form project_dissemination_forms object from 6.9, 6.10, 6.11 fields
        const projectDisseminationForms: {
            stimulated_individuals_count?: number | null;
            project_results_for_individuals?: string[] | null;
            participation_trend?: string | null;
        } = {};

        if (values.stimulated_individuals_count !== undefined && values.stimulated_individuals_count !== null) {
            projectDisseminationForms.stimulated_individuals_count = Number(values.stimulated_individuals_count) || null;
        }

        if (values.project_results_for_individuals && values.project_results_for_individuals.length > 0) {
            projectDisseminationForms.project_results_for_individuals = values.project_results_for_individuals;
        }

        if (values.participation_trend) {
            projectDisseminationForms.participation_trend = values.participation_trend;
        }

        const requestData: any = {
            ...(projectsPartnership?.id && { id: projectsPartnership.id }),
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
            project_types: projectTypes.length > 0 ? projectTypes : null,
            project_roles_ratings: Object.keys(rolesRatings).length > 0 ? rolesRatings : null,
            project_objectives: values.project_objectives && values.project_objectives.length > 0 ? values.project_objectives : null,
            partners: values.partners && values.partners.length > 0 ? values.partners : null,
            partnership_networks: Object.keys(partnershipNetworks).length > 0 ? partnershipNetworks : null,
            stimulated_organizations_stats: Object.keys(orgStats).length > 0 ? orgStats : null,
            project_results_for_organizations: values.project_results_for_organizations && values.project_results_for_organizations.length > 0 ? values.project_results_for_organizations : null,
            project_dissemination_forms: Object.keys(projectDisseminationForms).length > 0 ? projectDisseminationForms : null,
            project_evaluation_methods: values.project_evaluation_methods && values.project_evaluation_methods.length > 0 ? values.project_evaluation_methods : null,
        };

        console.log('Sending requestData:', requestData);

        await dispatch(createOrUpdateProjectsPartnership(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getProjectsPartnership({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    // Project types table columns
    const projectTypesColumns = [
        {
            title: 'Projektų tipai',
            dataIndex: 'title',
            key: 'title',
            width: '50%',
        },
        {
            title: 'Projektų skaičius',
            dataIndex: 'projects_count',
            key: 'projects_count',
            width: '25%',
            render: (_value: any, record: any, index: number) => (
                <Form.Item
                    name={['project_types', index, 'projects_count']}
                    style={{ margin: 0 }}
                    initialValue={record.projects_count || 0}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
            ),
        },
        {
            title: 'Projektų partnerinių organizacijų skaičius',
            dataIndex: 'partners_count',
            key: 'partners_count',
            width: '25%',
            render: (_value: any, record: any, index: number) => (
                <Form.Item
                    name={['project_types', index, 'partners_count']}
                    style={{ margin: 0 }}
                    initialValue={record.partners_count || 0}>
                    <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
            ),
        },
    ];

    return (
        <ConfigProvider locale={locale}>
            <div className="school-projects-partnership">
                <Card title="Projektinė veikla ir partnerystės tinklai" className="projects-partnership-form-card" loading={isProjectsPartnershipFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {projectsPartnership?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {projectsPartnership.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRefreshClick}
                                loading={!!isProjectsPartnershipFetching}>
                                Atnaujinti
                            </Button>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        onValuesChange={() => {
                            // Use setTimeout to ensure form values are updated
                            setTimeout(() => {
                                const allValues = form.getFieldsValue();

                                // Update partnership networks scale total
                                const scale = allValues.partnership_networks_scale || {};
                                const scaleTotal = Object.values(scale).reduce((sum: number, value: any) => sum + (Number(value) || 0), 0);
                                setPartnershipNetworksScaleTotal(scaleTotal);

                                // Update partnership networks nature total
                                const nature = allValues.partnership_networks_nature || {};
                                const natureTotal = Object.values(nature).reduce((sum: number, value: any) => sum + (Number(value) || 0), 0);
                                setPartnershipNetworksNatureTotal(natureTotal);

                                // Update stimulated organizations count (from stimulated_organizations_stats.total)
                                // Use getFieldValue to get the current value, which preserves the object structure
                                const orgStats = form.getFieldValue('stimulated_organizations_stats') || {};
                                setStimulatedOrganizationsCount(orgStats.total || null);

                                // Update stimulated individuals count
                                setStimulatedIndividualsCount(allValues.stimulated_individuals_count || null);

                                // Update effectiveness research (stored in project_evaluation_methods field)
                                setEffectivenessResearch(allValues.project_evaluation_methods && allValues.project_evaluation_methods.length > 0 ? 'taip' : 'ne');

                                // Update project results for institution (stored in partners field)
                                setProjectResultsForInstitutionState(allValues.partners || []);

                                // Update project results for organizations
                                setProjectResultsForOrganizationsState(allValues.project_results_for_organizations || []);
                            }, 0);
                        }}
                        initialValues={{
                            project_types: defaultProjectTypes,
                            project_roles_ratings: {},
                            project_objectives: [],
                            partners: [],
                            partnership_networks_scale: {},
                            partnership_networks_nature: {},
                            stimulated_organizations_stats: {},
                            project_results_for_organizations: [],
                            stimulated_individuals_count: null,
                            project_results_for_individuals: [],
                            participation_trend: null,
                            project_evaluation_methods: [],
                        }}>

                        {/* 6.1 Kokiuose projektuose dalyvauja institucija? */}
                        <Card title="6.1 Kokiuose projektuose dalyvauja institucija?" style={{ marginBottom: 16 }}>
                            <Form.Item name="project_types" initialValue={defaultProjectTypes}>
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prevValues, currentValues) => prevValues.project_types !== currentValues.project_types}>
                                    {() => {
                                        const projectTypes = form.getFieldValue('project_types') || defaultProjectTypes;
                                        const totalProjects = projectTypes.reduce((sum: number, type: any) => sum + (type.projects_count || 0), 0);
                                        const totalPartners = projectTypes.reduce((sum: number, type: any) => sum + (type.partners_count || 0), 0);
                                        return (
                                            <Table
                                                columns={projectTypesColumns}
                                                dataSource={defaultProjectTypes}
                                                pagination={false}
                                                rowKey="code"
                                                bordered
                                                // eslint-disable-next-line react/no-unstable-nested-components
                                                summary={() => (
                                                    <Table.Summary fixed>
                                                        <Table.Summary.Row>
                                                            <Table.Summary.Cell index={0}>
                                                                <Text strong>6.1.5 Iš viso</Text>
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={1}>
                                                                <Text strong>{totalProjects}</Text>
                                                            </Table.Summary.Cell>
                                                            <Table.Summary.Cell index={2}>
                                                                <Text strong>{totalPartners}</Text>
                                                            </Table.Summary.Cell>
                                                        </Table.Summary.Row>
                                                    </Table.Summary>
                                                )} />
                                        );
                                    }}
                                </Form.Item>
                            </Form.Item>
                        </Card>

                        {/* 6.2 Institucijos vaidmenys projektinėje veikloje */}
                        <Card title="6.2 Institucijos vaidmenys projektinėje veikloje" style={{ marginBottom: 16 }}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                Įvertinkite balais nuo 0 iki 4 (4 – dažniausiai)
                            </Text>
                            <Form.Item name="project_roles_ratings">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {projectRoles.map((role) => (
                                        <Row key={role.code} gutter={16} align="middle">
                                            <Col span={16}>
                                                <Text>{role.title}</Text>
                                            </Col>
                                            <Col span={8}>
                                                <Form.Item
                                                    name={['project_roles_ratings', role.code]}
                                                    style={{ margin: 0 }}
                                                    initialValue={0}>
                                                    <Rate count={4} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ))}
                                </Space>
                            </Form.Item>
                        </Card>

                        {/* 6.3 Kokie institucijos per ataskaitinį laikotarpį vykdomų projektą tikslai? */}
                        <Card title="6.3 Kokie institucijos per ataskaitinį laikotarpį vykdomų projektą tikslai?" style={{ marginBottom: 16 }}>
                            <Form.Item name="project_objectives" label="Projektų tikslai">
                                <Checkbox.Group>
                                    <Space direction="vertical">
                                        {projectObjectives.map((objective) => (
                                            <Checkbox key={objective.code} value={objective.code}>
                                                {objective.title}
                                            </Checkbox>
                                        ))}
                                    </Space>
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* 6.4 Ką davė projektai, kuriuose dalyvavo institucija? */}
                        <Card title="6.4 Ką davė projektai, kuriuose dalyvavo institucija?" style={{ marginBottom: 16 }}>
                            <Form.Item name="partners" label="Projektų rezultatai">
                                <Checkbox.Group>
                                    <Space direction="vertical">
                                        {projectResultsForInstitution.map((result) => (
                                            <Checkbox key={result.code} value={result.code}>
                                                {result.title}
                                            </Checkbox>
                                        ))}
                                    </Space>
                                </Checkbox.Group>
                            </Form.Item>
                            {projectResultsForInstitutionState.includes('partnerystes_rysiai') ? (
                                <Form.Item name="partnerystes_rysiai_location" label="Partnerystės ryšių vieta">
                                    <Checkbox.Group>
                                        <Space direction="vertical">
                                            <Checkbox value="lietuvoje">6.4.5.1 Lietuvoje</Checkbox>
                                            <Checkbox value="uzsienyje">6.4.5.2 Užsienyje</Checkbox>
                                        </Space>
                                    </Checkbox.Group>
                                </Form.Item>
                            ) : null}
                        </Card>

                        {/* 6.5 Kokio masto partnerystės tinkluose dalyvaujate? */}
                        <Card title="6.5 Kokio masto partnerystės tinkluose dalyvaujate? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Form.Item name="partnership_networks_scale">
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prevValues, currentValues) => prevValues.partnership_networks_scale !== currentValues.partnership_networks_scale}>
                                    {() => {
                                        const scaleValues = form.getFieldValue('partnership_networks_scale') || {};
                                        const total = Object.values(scaleValues).reduce((sum: number, value: any) => sum + (Number(value) || 0), 0);
                                        return (
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                {partnershipNetworksScale.map((scale) => (
                                                    <Row key={scale.code} gutter={16} align="middle">
                                                        <Col span={16}>
                                                            <Text>{scale.title}</Text>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Form.Item
                                                                name={['partnership_networks_scale', scale.code]}
                                                                style={{ margin: 0 }}
                                                                initialValue={0}>
                                                                <InputNumber min={0} style={{ width: '100%' }} />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                ))}
                                                <Row gutter={16} align="middle">
                                                    <Col span={16}>
                                                        <Text strong>6.5.4 Iš viso</Text>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Text strong>{total}</Text>
                                                    </Col>
                                                </Row>
                                            </Space>
                                        );
                                    }}
                                </Form.Item>
                            </Form.Item>
                        </Card>

                        {/* 6.6 Kokio pobūdžio partnerystės tinkluose dalyvaujate? */}
                        <Card title="6.6 Kokio pobūdžio partnerystės tinkluose dalyvaujate? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Form.Item name="partnership_networks_nature">
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prevValues, currentValues) => prevValues.partnership_networks_nature !== currentValues.partnership_networks_nature}>
                                    {() => {
                                        const natureValues = form.getFieldValue('partnership_networks_nature') || {};
                                        const total = Object.values(natureValues).reduce((sum: number, value: any) => sum + (Number(value) || 0), 0);
                                        return (
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                {partnershipNetworksNature.map((nature) => (
                                                    <Row key={nature.code} gutter={16} align="middle">
                                                        <Col span={16}>
                                                            <Text>{nature.title}</Text>
                                                        </Col>
                                                        <Col span={8}>
                                                            <Form.Item
                                                                name={['partnership_networks_nature', nature.code]}
                                                                style={{ margin: 0 }}
                                                                initialValue={0}>
                                                                <InputNumber min={0} style={{ width: '100%' }} />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                ))}
                                                <Row gutter={16} align="middle">
                                                    <Col span={16}>
                                                        <Text strong>6.6.4 Iš viso</Text>
                                                    </Col>
                                                    <Col span={8}>
                                                        <Text strong>{total}</Text>
                                                    </Col>
                                                </Row>
                                            </Space>
                                        );
                                    }}
                                </Form.Item>
                            </Form.Item>
                        </Card>

                        {/* 6.7 Kiek organizacijų paskatinote dalyvauti projektuose? */}
                        <Card title="6.7 Kiek organizacijų paskatinote dalyvauti projektuose? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Form.Item name={['stimulated_organizations_stats', 'total']} label="Organizacijų skaičius">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                            {stimulatedOrganizationsCount && stimulatedOrganizationsCount > 0 ? (
                                <>
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong>Organizacijų statistika</Text>
                                    </div>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                name={['stimulated_organizations_stats', 'mokyklos']}
                                                label="Mokyklos"
                                                style={{ marginBottom: 16 }}>
                                                <InputNumber min={0} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name={['stimulated_organizations_stats', 'kitos_istaigos']}
                                                label="Kitos įstaigos"
                                                style={{ marginBottom: 16 }}>
                                                <InputNumber min={0} style={{ width: '100%' }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </>
                            ) : null}
                        </Card>

                        {/* 6.8 Ką davė projektai šioms organizacijoms? */}
                        {stimulatedOrganizationsCount && stimulatedOrganizationsCount > 0 ? (
                            <Card title="6.8 Ką davė projektai šioms organizacijoms?" style={{ marginBottom: 16 }}>
                                <Form.Item name="project_results_for_organizations" label="Projektų rezultatai">
                                    <Checkbox.Group>
                                        <Space direction="vertical">
                                            {projectResultsForOrganizations.map((result) => (
                                                <Checkbox key={result.code} value={result.code}>
                                                    {result.title}
                                                </Checkbox>
                                            ))}
                                        </Space>
                                    </Checkbox.Group>
                                </Form.Item>
                                {projectResultsForOrganizationsState.includes('partnerystes_rysiai') ? (
                                    <Form.Item name="partnerystes_rysiai_organizations_location" label="Partnerystės ryšių vieta">
                                        <Checkbox.Group>
                                            <Space direction="vertical">
                                                <Checkbox value="lietuvoje">6.8.5.1 Lietuvoje</Checkbox>
                                                <Checkbox value="uzsienyje">6.8.5.2 Užsienyje</Checkbox>
                                            </Space>
                                        </Checkbox.Group>
                                    </Form.Item>
                                ) : null}
                            </Card>
                        ) : null}

                        {/* 6.9 Kiek pavienių asmenų paskatinote dalyvauti projektuose? */}
                        <Card title="6.9 Kiek pavienių asmenų paskatinote dalyvauti projektuose? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Form.Item name="stimulated_individuals_count" label="Asmenų skaičius">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Card>

                        {/* 6.10 Ką davė projektai šiems asmenims? */}
                        {stimulatedIndividualsCount && stimulatedIndividualsCount > 0 ? (
                            <Card title="6.10 Ką davė projektai šiems asmenims?" style={{ marginBottom: 16 }}>
                                <Form.Item name="project_results_for_individuals" label="Projektų rezultatai">
                                    <Checkbox.Group>
                                        <Space direction="vertical">
                                            {projectResultsForIndividuals.map((result) => (
                                                <Checkbox key={result.code} value={result.code}>
                                                    {result.title}
                                                </Checkbox>
                                            ))}
                                        </Space>
                                    </Checkbox.Group>
                                </Form.Item>
                            </Card>
                        ) : null}

                        {/* 6.11 Pastaruoju metu institucijos dalyvavimas projektuose ir partnerystės tinkluose */}
                        <Card title="6.11 Pastaruoju metu institucijos dalyvavimas projektuose ir partnerystės tinkluose" style={{ marginBottom: 16 }}>
                            <Form.Item name="participation_trend" label="Dalyvavimo tendencija">
                                <Radio.Group>
                                    <Radio value="aktyveja">6.11.1 Aktyvėja</Radio>
                                    <Radio value="nekinta">6.11.2 Nekinta</Radio>
                                    <Radio value="silpneja">6.11.3 Silpnėja</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </Card>

                        {/* 6.12 Ar tiriamas projektinės veiklos efektyvumas? */}
                        <Card title="6.12 Ar tiriamas projektinės veiklos efektyvumas?" style={{ marginBottom: 16 }}>
                            <Form.Item name="project_evaluation_methods" label="Kaip tiriamas?">
                                <Checkbox.Group>
                                    <Space direction="vertical">
                                        <Checkbox value="ataskaitos">6.12.1 Ataskaitos</Checkbox>
                                        <Checkbox value="apklausos">6.12.2 Apklausos</Checkbox>
                                        <Checkbox value="isoriniai_vertinimai">6.12.3 Išoriniai vertinimai</Checkbox>
                                        <Checkbox value="rodikliu_analize">6.12.4 Rodiklių analizė</Checkbox>
                                        <Checkbox value="kita">6.12.5 Kita (įrašykite)</Checkbox>
                                    </Space>
                                </Checkbox.Group>
                            </Form.Item>
                        </Card>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScProjectsPartnership;
